import { BaseApiService } from './BaseApiService';
import { logger } from '../utils/logger';
import { env } from '../config/environment';
import type { NewsArticle } from '../types/market';

interface FinnhubNewsResponse {
  category: string;
  datetime: number;
  headline: string;
  id: number;
  image: string;
  related: string;
  source: string;
  summary: string;
  url: string;
}

export class FinnhubNewsService extends BaseApiService {
  private apiKey: string;

  constructor() {
    // Don't pass headers to avoid CORS issues - use query parameter instead
    super('https://finnhub.io/api/v1', {});
    
    if (!env.hasApiKey('finnhub')) {
      logger.warn('VITE_FINNHUB_API_KEY not configured - news functionality will be limited');
      this.apiKey = '';
    } else {
      this.apiKey = env.apis.finnhub;
    }
  }

  async getMarketNews(limit: number = 10): Promise<NewsArticle[]> {
    if (!this.apiKey) {
      throw new Error('News service is not configured. Please check your API key settings.');
    }

    try {
      const response = await this.get<FinnhubNewsResponse[]>(`/news?category=general&token=${this.apiKey}`);

      if (!Array.isArray(response)) {
        throw new Error('Unable to retrieve news data. Please try again later.');
      }

      return response
        .slice(0, limit)
        .map(article => ({
          title: article.headline,
          source: article.source,
          snippet: this.truncateText(article.summary, 200),
          url: article.url,
          published_at: new Date(article.datetime * 1000).toISOString(),
          sentiment: 'neutral' as const, // Finnhub doesn't provide sentiment
        }));
    } catch (error: any) {
      // Check if it's an API key error
      if (error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('Invalid API key')) {
        logger.warn('Finnhub API key appears to be invalid');
        throw new Error('News service authentication failed. Please check your API key configuration.');
      } else if (error?.message?.includes('429')) {
        logger.warn('Finnhub API rate limit exceeded');
        throw new Error('News service is temporarily unavailable due to rate limits. Please try again later.');
      } else if (error?.message?.includes('Unable to retrieve news data')) {
        // Re-throw our custom error messages
        throw error;
      } else {
        logger.error('Error fetching news from Finnhub', error);
        throw new Error('Unable to load news at this time. Please try again later.');
      }
    }
  }

  async getCompanyNews(symbol: string, limit: number = 10): Promise<NewsArticle[]> {
    if (!this.apiKey) {
      throw new Error(`News service is not configured. Unable to load news for ${symbol}.`);
    }

    try {
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - 7); // Last 7 days
      const toDate = new Date();

      const from = fromDate.toISOString().split('T')[0];
      const to = toDate.toISOString().split('T')[0];

      const response = await this.get<FinnhubNewsResponse[]>(
        `/company-news?symbol=${symbol}&from=${from}&to=${to}&token=${this.apiKey}`
      );

      if (!Array.isArray(response)) {
        throw new Error(`Unable to retrieve news for ${symbol}. Please try again later.`);
      }

      return response
        .slice(0, limit)
        .map(article => ({
          title: article.headline,
          source: article.source,
          snippet: this.truncateText(article.summary, 200),
          url: article.url,
          published_at: new Date(article.datetime * 1000).toISOString(),
          sentiment: 'neutral' as const,
        }));
    } catch (error: any) {
      // Check if it's an API key error
      if (error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('Invalid API key')) {
        logger.warn(`Finnhub API key appears to be invalid for ${symbol} news`);
        throw new Error(`News service authentication failed. Unable to load news for ${symbol}.`);
      } else if (error?.message?.includes('429')) {
        logger.warn(`Finnhub API rate limit exceeded for ${symbol} news`);
        throw new Error(`News service is temporarily unavailable. Please try again later.`);
      } else if (error?.message?.includes('Unable to retrieve news')) {
        // Re-throw our custom error messages
        throw error;
      } else {
        logger.error('Error fetching company news from Finnhub', error);
        throw new Error(`Unable to load news for ${symbol} at this time. Please try again later.`);
      }
    }
  }

  private truncateText(text: string, maxLength: number): string {
    if (!text || text.length <= maxLength) {
      return text || '';
    }
    return text.substring(0, maxLength).trim() + '...';
  }


}